import React from 'react';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import { Highlight } from '@tiptap/extension-highlight';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Mention } from '@tiptap/extension-mention';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import type { Extensions } from '@tiptap/react';
import { mergeAttributes, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';

import Tooltip from '~/components/core/Atomic/Tooltip';
import { ConditionalSection } from '~/components/core/Editor/extensions/ConditionalSection';
import EmbedImages from '~/components/core/Editor/extensions/EmbedImages';
import FontSize from '~/components/core/Editor/extensions/FontSize';
import { flattenOptions, getSuggestionConfig } from '~/components/core/Editor/MenuBar/suggestion/suggestion';
import type { DisplayConfigProps, SuggestionOption } from '~/components/core/Editor/types';
import TextWithMDBold from '~/components/TextWithBold/TextWithMDBold';

export const getExtensions = (
  suggestionOptions: SuggestionOption[],
  displayConfig: DisplayConfigProps,
  disabled?: boolean,
  saveAsMarkdown?: boolean
): Extensions => {
  const extensions: Extensions = [
    StarterKit,
    Paragraph.extend({
      parseHTML() {
        return [{ tag: 'div' }];
      },
      renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
      },
    }),
    TextStyle,
  ];

  if (disabled || displayConfig.textStyle) {
    extensions.push(Underline);
  }
  if (disabled || displayConfig.align) {
    extensions.push(
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      })
    );
  }

  if (disabled || displayConfig.textColor) {
    extensions.push(Highlight.configure({ multicolor: true }));
    extensions.push(Color);
  }

  if (disabled || displayConfig.font) {
    extensions.push(FontFamily);
    extensions.push(FontSize);
  }

  if (disabled || displayConfig.link || displayConfig.preserveLinks) {
    // Without the link button the extension only keeps existing links, so typing or pasting a URL adds none.
    const createsLinks = disabled || !!displayConfig.link;
    extensions.push(
      Link.configure({
        openOnClick: false,
        autolink: createsLinks,
        linkOnPaste: createsLinks,
      })
    );
  }

  if (disabled || displayConfig.suggestions) {
    extensions.push(getMentionExtensionConfig(suggestionOptions));
  }

  const ExtendedImage = getExtendedImageExtension();

  extensions.push(
    ExtendedImage.configure({
      inline: true,
      allowBase64: true,
    })
  );

  if (displayConfig.embedImages) {
    extensions.push(EmbedImages.configure({ allowedTypes: displayConfig.embedImages }));
  }

  if (displayConfig.conditionalSections) {
    extensions.push(ConditionalSection);
  }

  extensions.push(Table);
  extensions.push(TableRow);
  extensions.push(TableCell);
  extensions.push(TableHeader);

  if (saveAsMarkdown) {
    extensions.push(Markdown.configure({ html: false, breaks: true, transformPastedText: true }));
  }

  return extensions;
};

// code from tiptap-extension-resize-image - https://www.npmjs.com/package/tiptap-extension-resize-image
const ImageResize = Image.extend({
  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      style: {
        default: 'width: auto; height: auto; cursor: pointer;',
        parseHTML: (element) => {
          const width = element.getAttribute('width');
          return width ? `width: ${width}px; height: auto; cursor: pointer;` : `${element.style.cssText}`;
        },
      },
      title: {
        default: null,
      },
      loading: {
        default: null,
      },
      srcset: {
        default: null,
      },
      sizes: {
        default: null,
      },
      crossorigin: {
        default: null,
      },
      usemap: {
        default: null,
      },
      ismap: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
      referrerpolicy: {
        default: null,
      },
      longdesc: {
        default: null,
      },
      decoding: {
        default: null,
      },
      class: {
        default: null,
      },
      id: {
        default: null,
      },
      name: {
        default: null,
      },
      draggable: {
        default: true,
      },
      tabindex: {
        default: null,
      },
      'aria-label': {
        default: null,
      },
      'aria-labelledby': {
        default: null,
      },
      'aria-describedby': {
        default: null,
      },
    };
  },
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const {
        view,
        options: { editable },
      } = editor;
      const { style } = node.attrs;
      const $wrapper = document.createElement('div');
      const $container = document.createElement('div');
      const $img = document.createElement('img');
      const iconStyle = 'width: 24px; height: 24px; cursor: pointer;';
      const dispatchNodeView = () => {
        if (typeof getPos === 'function') {
          const newAttrs = {
            ...node.attrs,
            style: $img.style.cssText,
            width: $img.width,
            height: null, // i.e. "auto"
          };
          view.dispatch(view.state.tr.setNodeMarkup(getPos(), null, newAttrs));
        }
      };
      $wrapper.setAttribute('style', `display: inline-flex;`);
      $wrapper.appendChild($container);
      $container.setAttribute('style', `${style}`);
      $container.appendChild($img);
      Object.entries(node.attrs).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        $img.setAttribute(key, value);
      });
      if (!editable) return { dom: $img };
      const dotPosition = '-4px';
      const dotsPosition = [
        `top: ${dotPosition}; left: ${dotPosition}; cursor: nwse-resize;`,
        `top: ${dotPosition}; right: ${dotPosition}; cursor: nesw-resize;`,
        `bottom: ${dotPosition}; left: ${dotPosition}; cursor: nesw-resize;`,
        `bottom: ${dotPosition}; right: ${dotPosition}; cursor: nwse-resize;`,
      ];
      let isResizing = false;
      let startX: number, startWidth: number;
      $container.addEventListener('click', () => {
        //remove remaining dots and position controller
        if ($container.childElementCount > 3) {
          // removing all dots before re-rendering them
          while ($container.lastChild && $container.childElementCount > 1) {
            $container.removeChild($container.lastChild);
          }
        }
        $container.setAttribute('style', `position: relative; border: 1px dashed #6C6C6C; ${style} cursor: pointer;`);
        Array.from({ length: 4 }, (_, index) => {
          const $dot = document.createElement('div');
          $dot.setAttribute(
            'style',
            `position: absolute; width: 9px; height: 9px; border: 1.5px solid #6C6C6C; border-radius: 50%; ${dotsPosition[index]}`
          );
          const applyResize = (clientX: number) => {
            if (!isResizing) return;
            const deltaX = index % 2 === 0 ? -(clientX - startX) : clientX - startX;
            const newWidth = Math.max(startWidth + deltaX, 20);
            $container.style.width = newWidth + 'px';
            $img.style.width = newWidth + 'px';
            // width attribute is needed for broad email compatibility
            $img.width = newWidth;
            // Removing possibly set height attribute, as height should be set automatically, according to the image aspect ratio.
            $img.removeAttribute('height');
          };

          const startResize = (
            clientX: number,
            moveEvent: string,
            endEvent: string,
            getClientX: (e: Event) => number
          ) => {
            isResizing = true;
            startX = clientX;
            startWidth = $container.offsetWidth;
            const onMove = (e: Event) => applyResize(getClientX(e));
            const onEnd = () => {
              isResizing = false;
              dispatchNodeView();
              document.removeEventListener(moveEvent, onMove);
              document.removeEventListener(endEvent, onEnd);
            };
            document.addEventListener(moveEvent, onMove);
            document.addEventListener(endEvent, onEnd);
          };

          $dot.addEventListener('mousedown', (e) => {
            e.preventDefault();
            startResize(e.clientX, 'mousemove', 'mouseup', (e) => (e as MouseEvent).clientX);
          });
          $dot.addEventListener(
            'touchstart',
            (e) => {
              e.cancelable && e.preventDefault();
              startResize(e.touches[0].clientX, 'touchmove', 'touchend', (e) => (e as TouchEvent).touches[0].clientX);
            },
            { passive: false }
          );
          $container.appendChild($dot);
        });
      });
      document.addEventListener('click', (e) => {
        const $target = e.target;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const isClickInside = $container.contains($target) || $target.style.cssText === iconStyle;
        if (!isClickInside) {
          const containerStyle = $container.getAttribute('style');
          const newStyle =
            containerStyle === null || containerStyle === void 0
              ? void 0
              : containerStyle.replace('border: 1px dashed #6C6C6C;', '');
          newStyle && $container.setAttribute('style', newStyle);
          if ($container.childElementCount > 3) {
            // removing all dots when clicking outside the image
            while ($container.lastChild && $container.childElementCount > 1) {
              $container.removeChild($container.lastChild);
            }
          }
        }
      });
      return {
        dom: $wrapper,
      };
    };
  },
});

const getExtendedImageExtension = () => {
  return ImageResize.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        height: {
          default: null,
          parseHTML: (element) => element.getAttribute('height'),
          renderHTML: (attributes) => ({
            height: attributes.height,
          }),
        },
        width: {
          default: null,
          parseHTML: (element) => element.getAttribute('width'),
          renderHTML: (attributes) => ({
            width: attributes.width,
          }),
        },
      };
    },
  });
};

interface MentionNodeViewProps {
  node: {
    attrs: {
      id: string;
      label: string;
      groupPath?: string[];
    };
  };
}

/**
 * React component for rendering Mention nodes in TipTap editor.
 * Uses MUI Tooltip for consistent styling.
 */
const MentionNodeView: React.FC<MentionNodeViewProps> = ({ node }) => {
  const { id, label, groupPath } = node.attrs;
  const displayText = label || id;
  const fullPath = groupPath?.length ? [...groupPath, displayText] : [];
  const tooltipContent = fullPath.length > 0 ? fullPath.join(' › ') : '';

  const mentionElement = (
    <NodeViewWrapper
      as="span"
      className="rounded-full bg-slate-400 pb-2 pl-12 pr-12 pt-2"
      data-label={label}
      data-group-path={groupPath?.length ? JSON.stringify(groupPath) : undefined}
    >
      <TextWithMDBold text={displayText} />
    </NodeViewWrapper>
  );

  if (!tooltipContent) {
    return mentionElement;
  }

  return (
    <Tooltip title={tooltipContent} placement="bottom">
      {mentionElement}
    </Tooltip>
  );
};

const getMentionExtensionConfig = (suggestionOptions: SuggestionOption[]) => {
  // order matters, .extend() needs to happen before .configure()
  // this gets called only when mounting the editor, so any changes
  // here require to unmount and remount the editor to take place

  const transformBoldText = (text: string) => {
    const regex = /\*\*(.*?)\*\*/g;
    return text.split(regex).map((part, index) => {
      if (index % 2 === 1) {
        return ['span', { class: 'font-bold' }, part];
      }
      return part;
    });
  };

  return Mention.extend({
    addAttributes() {
      return {
        id: {
          default: null,
          parseHTML: (element) => element.getAttribute('id'),
          renderHTML: (attributes) => {
            if (!attributes.id) {
              return {};
            }
            return {
              id: attributes.id,
            };
          },
        },
        label: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-label'),
          renderHTML: (attributes) => {
            if (!attributes.label) {
              return {};
            }
            return {
              'data-label': attributes.label,
            };
          },
        },
        groupPath: {
          default: null,
          parseHTML: (element) => {
            const path = element.getAttribute('data-group-path');
            if (!path) return null;
            try {
              return JSON.parse(path);
            } catch {
              return null;
            }
          },
          renderHTML: (attributes) => {
            if (!attributes.groupPath || attributes.groupPath.length === 0) {
              return {};
            }
            return {
              'data-group-path': JSON.stringify(attributes.groupPath),
            };
          },
        },
        class: {
          default: 'bg-slate-400 rounded-full pl-12 pr-12 pt-2 pb-2',
        },
      };
    },
    parseHTML() {
      return [
        {
          tag: 'mention',
        },
      ];
    },
    renderHTML({ HTMLAttributes, node }) {
      const groupPath = node.attrs.groupPath;
      const displayText = node.attrs.label || node.attrs.id;
      const fullPath = groupPath && groupPath.length > 0 ? [...groupPath, displayText] : [];
      const titleAttr = fullPath.length > 0 ? { title: fullPath.join(' › ') } : {};

      return [
        'mention',
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, titleAttr),
        ...transformBoldText(node.attrs.label || node.attrs.id),
      ];
    },
    addNodeView() {
      return ReactNodeViewRenderer(MentionNodeView);
    },
  }).configure({
    HTMLAttributes: {
      class: 'mention',
    },
    suggestion: Array.isArray(suggestionOptions) ? getSuggestionConfig(flattenOptions(suggestionOptions)) : undefined,
  });
};
